import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, X, User, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SupportChatPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [userName, setUserName] = useState('User');
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  const connectWs = useCallback(() => {
    const token = document.cookie.split('; ').find(c => c.startsWith('support_token='))?.split('=')[1];
    if (!token) return;

    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/chat/agent?token=${token}&chat_id=${chatId}`);

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'init') {
        setMessages(data.messages || []);
        setUserName(data.user_name || 'User');
      } else if (data.type === 'message') {
        setMessages(prev => [...prev, data]);
      } else if (data.type === 'chat_closed') {
        setConnected(false);
      } else if (data.type === 'user_connected') {
        setMessages(prev => [...prev, { sender_type: 'system', message: 'User reconnected.' }]);
      }
    };
    wsRef.current = ws;
  }, [chatId]);

  useEffect(() => {
    connectWs();
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [chatId, connectWs]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = () => {
    if (!input.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'message', message: input.trim() }));
    setInput('');
  };

  const closeChat = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'close_chat' }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]" data-testid="support-chat-page">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/support')} className="text-white/50 hover:text-white p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
            {userName[0]?.toUpperCase() || 'U'}
          </div>
          <div>
            <p className="text-white font-medium">{userName}</p>
            <p className="text-white/30 text-xs">
              {connected ? 'Connected' : 'Disconnected'}
              <span className={`inline-block w-1.5 h-1.5 rounded-full ml-1.5 ${connected ? 'bg-emerald-400' : 'bg-red-400'}`} />
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={closeChat}
          className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" data-testid="close-chat-btn">
          <X className="w-3 h-3 mr-1" /> End Chat
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3">
        {messages.map((msg, i) => (
          <div key={msg.id || i} className={`flex ${msg.sender_type === 'agent' ? 'justify-end' : 'justify-start'}`}>
            {msg.sender_type === 'system' ? (
              <div className="text-center w-full py-2">
                <span className="text-white/30 text-xs bg-white/5 px-3 py-1 rounded-full">{msg.message}</span>
              </div>
            ) : (
              <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                msg.sender_type === 'agent'
                  ? 'bg-gradient-to-br from-cyan-500/30 to-purple-500/30 text-white'
                  : 'bg-white/5 text-white/80'
              }`}>
                {msg.sender_type === 'user' && (
                  <p className="text-amber-400 text-xs font-medium mb-1">{msg.sender_name || userName}</p>
                )}
                <p className="text-sm">{msg.message}</p>
                <p className="text-white/20 text-[10px] mt-1 text-right">
                  {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="pt-4 border-t border-white/10">
        <div className="flex items-center gap-2">
          <input
            data-testid="agent-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={connected ? "Type a reply..." : "Chat disconnected"}
            disabled={!connected}
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-cyan-500/50 disabled:opacity-50"
          />
          <button
            data-testid="agent-chat-send"
            onClick={sendMessage}
            disabled={!connected || !input.trim()}
            className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white disabled:opacity-30"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
